import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import {
	graphql,
	type PreloadedQuery,
	usePaginationFragment,
	usePreloadedQuery,
} from 'react-relay';

import type {
	TProductDataRegions,
	TProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import type { BackendExperiment, ExperimentConfig } from '@atlassian/search-experiment/types';

import { ProductKeys } from '../../../common/constants/product-keys';
import { ResultsStates } from '../../../common/constants/results';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { IAnalyticsSearchPageResults, SearchResultSharedProps } from '../../../common/types';
import { ResultsNone } from '../../../common/ui/results-none';
import ResultsPaginationFooter from '../../../common/ui/results-pagination-footer';
import { SearchResultsList } from '../../../common/ui/search-result-list';
import type { ResultShown } from '../../../common/utils/events/types';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import { useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import useDwellTime from '../../../controllers/dwell';
import type { SelectedFiltersReturnType } from '../../../controllers/filters/selected/get-selected-filters';

import type {
	searchResultsCql_AtlassianSearchPage$data,
	searchResultsCql_AtlassianSearchPage$key,
} from './__generated__/searchResultsCql_AtlassianSearchPage.graphql';
import type { SearchResultsCQLPaginationQuery } from './__generated__/SearchResultsCQLPaginationQuery.graphql';
import { areSearchResultsPropsEqual } from './are-search-results-props-equal';
import { SearchResultCQL } from './search-result-cql';
import { useRenderResultsOptimisation } from './use-render-results-optimisation';
import { useSearchResultsAnalytics } from './use-search-results-analytics';
import { useSearchResultsVitalStats } from './use-search-results-vital-stats';

const noop = () => {};

type Node = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<searchResultsCql_AtlassianSearchPage$data['confluence_search']>['edges']
		>[number]
	>['node']
>;

type Edge = {
	readonly node: Node;
};

export type SearchResultsCQLProps = SearchResultSharedProps & {
	availableProducts: ProductKeys[];
	backendExperimentRef: React.RefObject<BackendExperiment>;
	bodyRef: React.RefObject<HTMLDivElement> | undefined;
	clearReferrer: () => void | Promise<void>;
	experimentConfig: ExperimentConfig;
	getSelectedFilters: () => SelectedFiltersReturnType;
	isJiraIssueIndexed: boolean;
	maxResultsToShow: number | undefined;
	productDataRegions: TProductDataRegions | undefined;
	productEdition: TProductEdition | undefined;
	referrer: string | undefined;
	searchResultsCQLQueryReference: PreloadedQuery<any>;
	setMaxResultsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
	setResultsState: (state: ResultsStates) => void;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
};

type SearchResultsCQlDataProps = {
	edges: Edge[];
	isLoadingNext: boolean;
	isPaginatedQuery: boolean;
};

/**
 * Separated out the results logic and rendering as the relay fragment and pagination
 * adds significant bulk to the component, making it hard to read and test.
 */
const SearchResultCQLWithData = ({
	availableProducts,
	backendExperimentRef,
	bodyRef,
	clearReferrer,
	cloudId,
	edges,
	experimentConfig,
	getSelectedFilters,
	isLoadingNext,
	isJiraIssueIndexed,
	isPaginatedQuery,
	maxResultsToShow,
	productDataRegions,
	productEdition,
	referrer,
	searchQuery,
	searchResultsCQLQueryReference,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	setMaxResultsToShow,
	setResultsState,
	thirdPartyConfigs,
}: SearchResultsCQLProps & SearchResultsCQlDataProps) => {
	useDwellTime();

	useEffect(() => {
		setResultsState(edges.length === 0 ? ResultsStates.NO_RESULTS : ResultsStates.RESULTS);
	}, [edges, setResultsState]);

	const edgesToDisplay = useRenderResultsOptimisation({
		bodyRef,
		edges,
		maxResultsToShow,
		setMaxResultsToShow,
		queryReference: searchResultsCQLQueryReference,
	});

	useSearchResultsVitalStats({
		edges,
		searchSessionId,
		isCQL: true,
	});

	const intl = useIntl();
	const intentDetectionTriggered = useMemo(
		() => isIntentDetectionTriggeredForExperiment(primaryProduct, intl.locale),
		[primaryProduct, intl.locale],
	);
	const resultsForAnalytics = useMemo(() => transformEdgesToResultsForAnalytics(edges), [edges]);
	const resultsShown = useMemo(() => transformEdgesToResultsShown(edges), [edges]);
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	useSearchResultsAnalytics({
		are3pResultsShown: false,
		backendExperimentRef,
		clearReferrer,
		cloudId,
		commonAttributes,
		edges,
		experimentConfig,
		getSelectedFilters,
		intentDetectionTriggered,
		isJiraIssueIndexed,
		isLoadingNext,
		isPaginatedQuery,
		nonPrivacySafeAttributes,
		productDataRegions,
		productEdition,
		productsWithTimeoutsFromErrors: undefined,
		referrer,
		resultsForAnalytics,
		resultsShown,
		searchQuery,
		// CQL search is scoped to the current site, so there is no site selection to report.
		selectedSites: undefined,
	});

	return edges.length > 0 ? (
		<>
			{edgesToDisplay
				.flatMap((edge) => (edge?.node ? [edge.node] : []))
				.map((node, position) => {
					const key = node.content?.ari || node.url || String(position);
					return (
						<SearchResultCQL
							appMode={appMode}
							availableProducts={availableProducts}
							cloudId={cloudId}
							key={key}
							mainContentWidth={mainContentWidth}
							node={node}
							position={position}
							primaryProduct={primaryProduct}
							primarySubProduct={primarySubProduct}
							searchQuery={searchQuery}
							searchSessionId={searchSessionId}
							selectedProduct={selectedProduct}
							thirdPartyConfigs={thirdPartyConfigs}
						/>
					);
				})}
		</>
	) : (
		<ResultsNone filtersEnabled={false} clearFilters={noop} />
	);
};

const SearchResultsCQL = (props: SearchResultsCQLProps): React.JSX.Element => {
	const { searchResultsCQLQueryReference } = props;

	const preloadedQueryData = usePreloadedQuery(
		graphql`
			query searchResultsCqlQuery($cql: String!, $first: Int, $after: String) {
				...searchResultsCql_AtlassianSearchPage @arguments(first: $first, after: $after)
			}
		`,
		searchResultsCQLQueryReference,
	);

	// Pagination hasPrevious state (hasPreviousPage from usePaginationFragment does not work with aggregator)
	// See https://hello.atlassian.net/wiki/x/lYYyQQE
	const hasPreviousRef = useRef(false);

	const { data, loadNext, isLoadingNext } = usePaginationFragment<
		SearchResultsCQLPaginationQuery,
		searchResultsCql_AtlassianSearchPage$key
	>(
		graphql`
			fragment searchResultsCql_AtlassianSearchPage on Query
			@refetchable(queryName: "SearchResultsCQLPaginationQuery")
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 30 }
				after: { type: "String", defaultValue: null }
			) {
				confluence_search(cql: $cql, first: $first, after: $after)
					@connection(key: "SearchResultsCQLPagination__confluence_search") {
					edges {
						node {
							__typename
							url
							content {
								ari
							}
							...searchResultCql_AtlassianSearchPage
						}
					}
					pageInfo {
						endCursor
					}
				}
			}
		`,
		preloadedQueryData,
	);

	const cursor = data.confluence_search?.pageInfo?.endCursor;

	const fetchNextPage = useCallback(
		(nextCursor: string) => {
			// Double check we're fetching with the cursor from our current results
			if (nextCursor === cursor) {
				loadNext(30);
				hasPreviousRef.current = true;
			}
		},
		[loadNext, cursor],
	);

	const edges: Edge[] = useMemo(() => {
		const rawEdges = data.confluence_search?.edges || [];
		return rawEdges.flatMap((edge) => (edge?.node ? [{ node: edge.node }] : []));
	}, [data.confluence_search?.edges]);

	return (
		<SearchResultsList containsSearchResults={edges.length > 0}>
			<SearchResultCQLWithData
				{...props}
				edges={edges}
				isLoadingNext={isLoadingNext}
				isPaginatedQuery={hasPreviousRef.current}
			/>
			{/* TODO: Check what happens when pagination returns no results, skeleton should disappear */}
			<ResultsPaginationFooter
				cursor={cursor}
				isLoading={isLoadingNext}
				fetchNextPage={fetchNextPage}
			/>
		</SearchResultsList>
	);
};

const MemoizedSearchResultsCQL: React.NamedExoticComponent<SearchResultsCQLProps> = React.memo(
	SearchResultsCQL,
	areSearchResultsPropsEqual,
);

export { MemoizedSearchResultsCQL as SearchResultsCQL };

const transformEdgesToResultsForAnalytics = (edges: Edge[]): IAnalyticsSearchPageResults => {
	return {
		edges: edges.map((edge) => ({
			node: {
				id: edge.node.content?.ari || '',
				__typename: edge.node.__typename,
				integrationId: '',
				providerId: '',
			},
		})),
	};
};

const transformEdgesToResultsShown = (edges: Edge[]): ResultShown[] => {
	return edges.map((edge, index) => ({
		id: edge.node.content?.ari || '',
		product: ProductKeys.Confluence,
		position: index,
		metadata: {},
	}));
};
